import React, { useEffect, useState } from "react";
import IncludeSideBar from "../../Components/Sidebar/IncludeSideBar";
import "./HelpAndSupport.css";

const helpAndSupportApi = require("../../apis/help_and_support");

const defaultFormState = {
  support_emails: [""],
  phone_support: "",
  phone_support_label: "",
  phone_support_note: "",
  whatsapp_support: "",
  customer_support_hours: [
    { label: "Monay to Friday", value: "" },
    { label: "Saturday & Sunday", value: "" },
  ],
};

const extractSettingsPayload = (response) => {
  if (response?.data) {
    return response.data;
  }

  return response || {};
};

const mapSupportHoursForForm = (hours) => {
  if (!Array.isArray(hours) || !hours.length) {
    return defaultFormState.customer_support_hours;
  }

  const mappedHours = hours
    .map((item) => {
      const [label = "", value = ""] = Object.entries(item || {})[0] || [];
      return { label, value };
    })
    .filter((item) => item.label || item.value);

  return mappedHours.length
    ? mappedHours
    : defaultFormState.customer_support_hours;
};

const mapSettingsToForm = (settings) => ({
  support_emails:
    Array.isArray(settings?.support_emails) && settings.support_emails.length
      ? settings.support_emails
      : defaultFormState.support_emails,
  phone_support: settings?.phone_support || "",
  phone_support_label: settings?.phone_support_label || "",
  phone_support_note: settings?.phone_support_note || "",
  whatsapp_support: settings?.whatsapp_support || "",
  customer_support_hours: mapSupportHoursForForm(
    settings?.customer_support_hours
  ),
});

const buildPayload = (formData) => ({
  support_emails: formData.support_emails
    .map((email) => email.trim())
    .filter(Boolean),
  phone_support: formData.phone_support.trim(),
  phone_support_label: formData.phone_support_label.trim(),
  phone_support_note: formData.phone_support_note.trim(),
  whatsapp_support: formData.whatsapp_support.trim(),
  customer_support_hours: formData.customer_support_hours
    .map((item) => ({
      label: item.label.trim(),
      value: item.value.trim(),
    }))
    .filter((item) => item.label && item.value)
    .map((item) => ({
      [item.label]: item.value,
    })),
});

const HelpAndSupport = () => {
  const [formData, setFormData] = useState(defaultFormState);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  const fetchSettings = async () => {
    setIsLoading(true);
    try {
      const response = await helpAndSupportApi.getHelpAndSupportSettings();
      const settings = extractSettingsPayload(response);
      setFormData(mapSettingsToForm(settings));
    } catch (error) {
      console.error("Error fetching help and support settings:", error);
      alert("Unable to load help and support settings.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  const updateField = (field, value) => {
    setFormData((prevState) => ({
      ...prevState,
      [field]: value,
    }));
  };

  const handleEmailChange = (index, value) => {
    const updatedEmails = [...formData.support_emails];
    updatedEmails[index] = value;
    updateField("support_emails", updatedEmails);
  };

  const addEmailField = () => {
    updateField("support_emails", [...formData.support_emails, ""]);
  };

  const removeEmailField = (index) => {
    const updatedEmails = formData.support_emails.filter(
      (_, emailIndex) => emailIndex !== index
    );

    updateField(
      "support_emails",
      updatedEmails.length ? updatedEmails : [""]
    );
  };

  const handleHourChange = (index, field, value) => {
    const updatedHours = [...formData.customer_support_hours];
    updatedHours[index] = {
      ...updatedHours[index],
      [field]: value,
    };
    updateField("customer_support_hours", updatedHours);
  };

  const addHourRow = () => {
    updateField("customer_support_hours", [
      ...formData.customer_support_hours,
      { label: "", value: "" },
    ]);
  };

  const removeHourRow = (index) => {
    const updatedHours = formData.customer_support_hours.filter(
      (_, rowIndex) => rowIndex !== index
    );

    updateField(
      "customer_support_hours",
      updatedHours.length ? updatedHours : [{ label: "", value: "" }]
    );
  };

  const handleSubmit = async () => {
    setIsSaving(true);
    try {
      await helpAndSupportApi.updateHelpAndSupportSettings(
        buildPayload(formData)
      );
      alert("Help and support settings updated successfully.");
      await fetchSettings();
    } catch (error) {
      console.error("Error updating help and support settings:", error);
      alert("Unable to update help and support settings.");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <IncludeSideBar>
      <div className="help-support-container">
        <div className="Header">Help & Support</div>

        {isLoading ? (
          <div className="help-support-card">
            <p className="help-support-status">Loading support settings...</p>
          </div>
        ) : (
          <div className="help-support-card">
            <div className="help-support-section">
              <h3>Support Emails</h3>
              {formData.support_emails.map((email, index) => (
                <div className="help-support-row" key={`email-${index}`}>
                  <input
                    type="email"
                    placeholder="support@example.com"
                    value={email}
                    onChange={(event) =>
                      handleEmailChange(index, event.target.value)
                    }
                  />
                  <button
                    className="help-support-button secondary"
                    onClick={() => removeEmailField(index)}
                    type="button"
                  >
                    Remove
                  </button>
                </div>
              ))}
              <button
                className="help-support-button secondary"
                onClick={addEmailField}
                type="button"
              >
                Add Email
              </button>
            </div>

            <div className="help-support-section">
              <h3>Phone & WhatsApp</h3>
              <div className="help-support-grid">
                <div className="help-support-field">
                  <label>Phone Support</label>
                  <input
                    type="text"
                    value={formData.phone_support}
                    onChange={(event) =>
                      updateField("phone_support", event.target.value)
                    }
                  />
                </div>

                <div className="help-support-field">
                  <label>Phone Support Label</label>
                  <input
                    type="text"
                    value={formData.phone_support_label}
                    onChange={(event) =>
                      updateField("phone_support_label", event.target.value)
                    }
                  />
                </div>

                <div className="help-support-field">
                  <label>WhatsApp Support</label>
                  <input
                    type="text"
                    value={formData.whatsapp_support}
                    onChange={(event) =>
                      updateField("whatsapp_support", event.target.value)
                    }
                  />
                </div>
              </div>

              <div className="help-support-field">
                <label>Phone Support Note</label>
                <textarea
                  value={formData.phone_support_note}
                  onChange={(event) =>
                    updateField("phone_support_note", event.target.value)
                  }
                />
              </div>
            </div>

            <div className="help-support-section">
              <h3>Customer Support Hours</h3>
              {formData.customer_support_hours.map((item, index) => (
                <div className="help-support-hour-row" key={`hour-${index}`}>
                  <input
                    type="text"
                    placeholder="Day label"
                    value={item.label}
                    onChange={(event) =>
                      handleHourChange(index, "label", event.target.value)
                    }
                  />
                  <input
                    type="text"
                    placeholder="09:00 AM - 06:00 PM"
                    value={item.value}
                    onChange={(event) =>
                      handleHourChange(index, "value", event.target.value)
                    }
                  />
                  <button
                    className="help-support-button secondary"
                    onClick={() => removeHourRow(index)}
                    type="button"
                  >
                    Remove
                  </button>
                </div>
              ))}

              <button
                className="help-support-button secondary"
                onClick={addHourRow}
                type="button"
              >
                Add Hours Row
              </button>
            </div>

            <div className="help-support-actions">
              <button
                className="help-support-button"
                disabled={isSaving}
                onClick={handleSubmit}
                type="button"
              >
                {isSaving ? "Saving..." : "Save Settings"}
              </button>

              <button
                className="help-support-button secondary"
                disabled={isSaving}
                onClick={fetchSettings}
                type="button"
              >
                Refresh
              </button>
            </div>
          </div>
        )}
      </div>
    </IncludeSideBar>
  );
};

export default HelpAndSupport;
